/**
 * First-post nudge for brand-new users.
 *
 * Phase 1 slice 4 of launch-readiness — after a user completes
 * onboarding they often land on a feed they didn't pick the
 * authors for. Telling them "post your first thing" with three
 * one-tap starters converts the most when they're warm.
 *
 * Templates intentionally stay generic. Picking topical templates
 * means we'd need to refresh them; generic prompts are evergreen
 * and work for any niche.
 *
 * Visibility: only when the viewer has zero published scoops. We
 * cache the answer in localStorage so we don't re-check on every
 * dashboard mount; if the answer is "yes posted" it never flips
 * back, so we can stop checking after the first hit.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const STORAGE_KEY = 'soltik:has_posted';
const DISMISSED_KEY = 'soltik:first_post_dismissed';

interface Props {
  /** Pass-through to the existing composer. Triggered when the user
   *  taps a template — we want them to land on a focused composer
   *  with the template prefilled. */
  onUseTemplate: (text: string) => void;
}

interface Template {
  emoji: string;
  titleKey: string;
  titleFallback: string;
  bodyKey: string;
  bodyFallback: string;
}

const TEMPLATES: Template[] = [
  {
    emoji: '\uD83D\uDC4B', // 👋
    titleKey: 'firstPost.intro.title',
    titleFallback: 'Say hi',
    bodyKey: 'firstPost.intro.body',
    bodyFallback: "Hi everyone — I'm new to Soltik. I'm into ",
  },
  {
    emoji: '\uD83D\uDCA1', // 💡
    titleKey: 'firstPost.thought.title',
    titleFallback: 'Share a thought',
    bodyKey: 'firstPost.thought.body',
    bodyFallback: "Something I've been thinking about lately: ",
  },
  {
    emoji: '\uD83D\uDD17', // 🔗
    titleKey: 'firstPost.link.title',
    titleFallback: 'Share a link',
    bodyKey: 'firstPost.link.body',
    bodyFallback: 'Worth reading: ',
  },
];

export default function FirstPostPrompt({ onUseTemplate }: Props) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISSED_KEY) === '1') return;
    if (localStorage.getItem(STORAGE_KEY) === '1') return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    let userId: string | null;
    try {
      userId = JSON.parse(atob(token.split('.')[1])).sub || null;
    } catch { return; }
    if (!userId) return;

    // Cheap check: get one scoop from the user. If any, they've
    // posted before; cache that fact and never check again.
    fetch(`${API_URL}/scoops/user/${userId}?limit=1`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const count = Array.isArray(data?.scoops) ? data.scoops.length : 0;
        if (count > 0) {
          localStorage.setItem(STORAGE_KEY, '1');
        } else {
          setVisible(true);
        }
      })
      .catch(() => { /* silent — prompt just won't show */ });
  }, []);

  if (!visible) return null;

  return (
    <div className="mb-6 p-5 rounded-2xl bg-linear-to-br/srgb from-iris-500/10 to-iris-500/5 border border-iris-500/20 relative">
      <button
        type="button"
        onClick={() => {
          sessionStorage.setItem(DISMISSED_KEY, '1');
          setVisible(false);
        }}
        aria-label={t('firstPost.dismiss', 'Dismiss')}
        className="absolute top-3 right-3 p-1 rounded-md text-[#a0a0b0] hover:text-white hover:bg-white/5"
      >
        <X size={16} />
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} className="text-iris-400" />
        <h3 className="text-sm font-semibold text-white">
          {t('firstPost.title', 'Post your first thing')}
        </h3>
      </div>
      <p className="text-xs text-[#a0a0b0] mb-4">
        {t('firstPost.subtitle', "The fastest way to get followers is to start posting. Pick a template — you can edit it before you post.")}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {TEMPLATES.map((tpl, i) => (
          <button
            key={i}
            type="button"
            onClick={() => {
              onUseTemplate(t(tpl.bodyKey, tpl.bodyFallback));
              // Don't dismiss yet — they might bail out of the
              // composer and want to try another template.
            }}
            className="text-left p-3 rounded-xl bg-[#0a0a12] border border-white/8 hover:border-iris-500/40 hover:bg-[#10101a] transition-colors"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-lg">{tpl.emoji}</span>
              <span className="text-xs font-medium text-white">
                {t(tpl.titleKey, tpl.titleFallback)}
              </span>
            </div>
            <p className="text-[11px] text-[#8a8a9a] line-clamp-2">
              {t(tpl.bodyKey, tpl.bodyFallback)}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Call this once after a successful post to mark the user as
 *  having posted, so the prompt never re-appears. */
export function markUserHasPosted() {
  localStorage.setItem(STORAGE_KEY, '1');
}
